import { httpClient } from '../httpClient';
import {
  ICreativeConversation,
  ICreativeMessage,
  CreateConversationRequest,
  UpdateConversationRequest,
} from '@packages/global';

/**
 * 创意聊天 API 模块
 * 处理创意会话的 CRUD 操作和 SSE 流式消息
 */
export class CreativeChatApi {
  private basePath = '/user/creative-conversations';

  /**
   * 获取会话列表
   */
  async getConversations(): Promise<ICreativeConversation[]> {
    return httpClient.get<ICreativeConversation[]>(this.basePath);
  }

  /**
   * 创建会话
   */
  async createConversation(data: CreateConversationRequest): Promise<ICreativeConversation> {
    return httpClient.post<ICreativeConversation>(this.basePath, data);
  }

  /**
   * 更新会话
   */
  async updateConversation(id: string, data: UpdateConversationRequest): Promise<ICreativeConversation> {
    return httpClient.put<ICreativeConversation>(`${this.basePath}/${id}`, data);
  }

  /**
   * 删除会话
   */
  async deleteConversation(id: string): Promise<void> {
    return httpClient.delete<void>(`${this.basePath}/${id}`);
  }

  /**
   * 获取会话消息列表
   */
  async getMessages(conversationId: string): Promise<ICreativeMessage[]> {
    return httpClient.get<ICreativeMessage[]>(`${this.basePath}/${conversationId}/messages`);
  }

  /**
   * 删除单条消息
   */
  async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    return httpClient.delete<void>(`${this.basePath}/${conversationId}/messages/${messageId}`);
  }

  /**
   * 清空会话所有消息
   */
  async clearMessages(conversationId: string): Promise<void> {
    return httpClient.delete<void>(`${this.basePath}/${conversationId}/messages`);
  }

  /**
   * SSE 流式发送消息
   * 使用 fetch + ReadableStream（非 EventSource，因为需要 POST + 自定义 headers）
   * 返回包含 abort 方法的对象，用于取消请求
   */
  streamMessage(
    conversationId: string,
    content: string,
    modelId?: string,
    callbacks?: {
      onStart?: () => void;
      onDelta?: (content: string) => void;
      onDone?: (content: string) => void;
      onError?: (error: string) => void;
    }
  ): { abort: () => void } {
    const controller = new AbortController();
    const token = localStorage.getItem('token');

    const run = async () => {
      try {
        const response = await fetch(`/api${this.basePath}/${conversationId}/messages/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ content, modelId }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          callbacks?.onError?.('请求失败');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                switch (data.type) {
                  case 'start':
                    callbacks?.onStart?.();
                    break;
                  case 'delta':
                    callbacks?.onDelta?.(data.content);
                    break;
                  case 'done':
                    callbacks?.onDone?.(data.content);
                    break;
                  case 'error':
                    callbacks?.onError?.(data.error);
                    break;
                }
              } catch {
                // ignore parse errors
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          callbacks?.onError?.(error.message || '网络错误');
        }
      }
    };

    run();

    return { abort: () => controller.abort() };
  }
}

// 导出单例实例
export const creativeChatApi = new CreativeChatApi();
